// src/helper/appointmentConstants.js

import EnvironmentService from "../../../../../service/environment/EnvironmentService";

const environmentService = new EnvironmentService();

export const LOCATION_MODE = {
  GOOGLE_MAPS: "GOOGLE_MAPS",
  MANUAL: "MANUAL",
  SAVED_ADDRESSES: "SAVED_ADDRESSES",
};

// --------------------
// Navigo Appointment Status Enum
// --------------------
export const NAVIGO_STATUS = Object.freeze({
  SCHEDULED_LEGACY: 0,
  IN_PROGRESS: 1,
  COMPLETED: 2,
  CANCELLED: 3,
  CONFIRMED: 4,
  SCHEDULED: 5,
  RESCHEDULE_REQUESTED: 6,
  PHLEBO_ASSIGNED: 7,
  PICKUP: 8,
  SAMPLE_COLLECTED: 9,
  SAMPLE_RECEIVED: 10,
  REPORT_GENERATED: 11,
  SUCCESSFUL_HRA: 12,
  SUCCESSFUL_PRC: 13,
  DECLINED: 14,
  NOT_ELIGIBLE: 15,
  NOT_ANSWERED: 16,
  NOT_ANSWERED_3_ATTEMPTS: 17,
  CALL_LATER: 18,
  NOT_REACHABLE: 19,
  REQUEST_TO_LAB: 20,
  NO_SHOW: 21,
  LAB_VISITED: 22,
});

// --------------------
// Service Type Enum
// --------------------
export const SERVICE_TYPE = Object.freeze({
  CONSULTATION: 1,
  HEALTH_CHECKUP: 2,
  VACCINE: 3,
  GYM: 4,
  DENTAL: 5,
});

// --------------------
// Service Modes Enum
// --------------------
export const MODES = Object.freeze({
  TELE_CONSULTATION: "Tele consultation",
  VIDEO_CONSULTATION: "Video consultation",
  IN_PERSON_CONSULTATION: "In person consultation",
  HOME_SAMPLE: "Home sample",
  CENTRE_VISIT: "Centre visit",
  CAMP_SITE_VISIT: "Camp site visit",
  SUBSCRIPTION: "Subscription",
});

// Location-based modes — require state/city/geo filters
export const LOCATION_BASED_MODES = [
  MODES.IN_PERSON_CONSULTATION,
  MODES.CENTRE_VISIT,
  MODES.CAMP_SITE_VISIT,
];

// Modes that allow auto service provider selection
export const MODES_WITH_AUTO_SP = [
  MODES.VIDEO_CONSULTATION,
  MODES.TELE_CONSULTATION,
  MODES.HOME_SAMPLE,
];

// --------------------
// Service Sub Type Enum
// --------------------
export const SERVICE_SUB_TYPE = Object.freeze({
  AVAIL_CARE: 3,
});

// --------------------
// Appointment Payment Status Enum
// --------------------
export const APPOINTMENT_PAYMENT_STATUS = Object.freeze({
  PENDING: 0,
  INITIATED: 1,
  COMPLETE: 2,
  FAILED: 3,
  SPONSORED: 4,
  // LINK_NOT_GENERATED: 5, //removed - no use
  LINK_EXPIRED: 6,
  LINK_CANCELLED: 7,
  REFUNDED: 8,
});

// --------------------
// Payment Status Enum (for appointment.paymentStatus field)
// --------------------
export const NEW_PAYMENT_STATUS = Object.freeze({
  UNKNOWN: 0, // Old appointment records which do not have payment status will be considered as unknown
  SPONSORED: 1,
  PENDING: 2,
  SUCCESSFUL: 3,
});

// --------------------
// Refund Status Enum
// --------------------
export const REFUND_STATUS = Object.freeze({
  NO_REFUND: 1,
  ELIGIBLE_APPLICABLE: 2,
  ELIGIBLE_NOT_APPLICABLE: 3,
  REFUND_EXIST: 4, // fully refunded — nothing left to refund
  FORCED: 5, // refundRequest override: pay out even though not applicable
  PARTIALLY_REFUNDED: 6, // a refund exists but the balance is still refundable
});

// --------------------
// Cancel Appointment Option Enum
// --------------------
export const CANCEL_APP_OPTION = Object.freeze({
  NONE: 0,
  JUST_CANCEL: 1,
  CANCEL_WITH_REFUND: 2,
  CANCEL_WITHOUT_REFUND: 3,
});

// --------------------
// Full Status List
// --------------------
export const statusList = [
  { value: NAVIGO_STATUS.SCHEDULED_LEGACY, name: "Scheduled" },
  { value: NAVIGO_STATUS.IN_PROGRESS, name: "In progress" },
  { value: NAVIGO_STATUS.COMPLETED, name: "Completed" },
  { value: NAVIGO_STATUS.CANCELLED, name: "Cancelled" },
  { value: NAVIGO_STATUS.CONFIRMED, name: "Confirmed" },
  { value: NAVIGO_STATUS.SCHEDULED, name: "Scheduled" },
  { value: NAVIGO_STATUS.RESCHEDULE_REQUESTED, name: "Reschedule Requested" },
  { value: NAVIGO_STATUS.PHLEBO_ASSIGNED, name: "Phlebo Assigned" },
  { value: NAVIGO_STATUS.PICKUP, name: "Pickup" },
  { value: NAVIGO_STATUS.SAMPLE_COLLECTED, name: "Sample Collected" },
  { value: NAVIGO_STATUS.SAMPLE_RECEIVED, name: "Sample Received" },
  { value: NAVIGO_STATUS.REPORT_GENERATED, name: "Report Generated" },
  { value: NAVIGO_STATUS.SUCCESSFUL_HRA, name: "Successful HRA" },
  { value: NAVIGO_STATUS.SUCCESSFUL_PRC, name: "Successful PRC" },
  { value: NAVIGO_STATUS.DECLINED, name: "Declined" },
  { value: NAVIGO_STATUS.NOT_ELIGIBLE, name: "Not eligible" },
  { value: NAVIGO_STATUS.NOT_ANSWERED, name: "Not answered" },
  {
    value: NAVIGO_STATUS.NOT_ANSWERED_3_ATTEMPTS,
    name: "Not answered - 3 attempts",
  },
  { value: NAVIGO_STATUS.CALL_LATER, name: "Call later" },
  { value: NAVIGO_STATUS.NOT_REACHABLE, name: "Not reachable" },
  { value: NAVIGO_STATUS.REQUEST_TO_LAB, name: "Request to lab" },
  { value: NAVIGO_STATUS.NO_SHOW, name: "No show" },
  { value: NAVIGO_STATUS.LAB_VISITED, name: "Lab visited" },
];

// End status values — appointments with these statuses are considered terminal
export const END_STATUS_SET = new Set([
  NAVIGO_STATUS.COMPLETED,
  NAVIGO_STATUS.CANCELLED,
  NAVIGO_STATUS.REPORT_GENERATED,
  NAVIGO_STATUS.SUCCESSFUL_HRA,
  NAVIGO_STATUS.SUCCESSFUL_PRC,
  NAVIGO_STATUS.DECLINED,
  NAVIGO_STATUS.NOT_ELIGIBLE,
  NAVIGO_STATUS.NO_SHOW,
]);
// Completed end statuses
export const COMPLETED_STATUS_SET = new Set([
  NAVIGO_STATUS.COMPLETED,
  NAVIGO_STATUS.REPORT_GENERATED,
  NAVIGO_STATUS.SUCCESSFUL_HRA,
  NAVIGO_STATUS.SUCCESSFUL_PRC,
]);

// Cancelled / failed end statuses
export const CANCELLED_STATUS_SET = new Set([
  NAVIGO_STATUS.CANCELLED,
  NAVIGO_STATUS.DECLINED,
  NAVIGO_STATUS.NOT_ELIGIBLE,
  NAVIGO_STATUS.NO_SHOW,
]);

// --------------------
// Status lists for different service types
// --------------------
export const generalNavigoStatus = [];

export const navigoLabtestStatus = [
  { value: NAVIGO_STATUS.SCHEDULED, name: "Scheduled" },
  { value: NAVIGO_STATUS.CONFIRMED, name: "Confirmed" },
  { value: NAVIGO_STATUS.RESCHEDULE_REQUESTED, name: "Reschedule Requested" },
  { value: NAVIGO_STATUS.PHLEBO_ASSIGNED, name: "Phlebo Assigned" },
  { value: NAVIGO_STATUS.PICKUP, name: "Pickup" },
  { value: NAVIGO_STATUS.SAMPLE_COLLECTED, name: "Sample Collected" },
  { value: NAVIGO_STATUS.SAMPLE_RECEIVED, name: "Sample Received" },
  { value: NAVIGO_STATUS.REPORT_GENERATED, name: "Report Generated" },
  { value: NAVIGO_STATUS.COMPLETED, name: "Completed" },
];

export const hraStatuses = [
  { value: NAVIGO_STATUS.CALL_LATER, name: "Call later" },
  { value: NAVIGO_STATUS.REQUEST_TO_LAB, name: " Request to lab" },
  { value: NAVIGO_STATUS.LAB_VISITED, name: "Lab visited" },
  { value: NAVIGO_STATUS.IN_PROGRESS, name: "In progress" },
  { value: NAVIGO_STATUS.CONFIRMED, name: "Confirmed" },
  { value: NAVIGO_STATUS.NOT_ANSWERED, name: "Not answered" },
  {
    value: NAVIGO_STATUS.NOT_ANSWERED_3_ATTEMPTS,
    name: "Not answered - 3 attempts",
  },
  { value: NAVIGO_STATUS.NOT_REACHABLE, name: "Not reachable" },
  { value: NAVIGO_STATUS.NOT_ELIGIBLE, name: "Not eligible" },
  { value: NAVIGO_STATUS.COMPLETED, name: "Completed" },
  { value: NAVIGO_STATUS.SUCCESSFUL_HRA, name: "Successful HRA" },
  { value: NAVIGO_STATUS.SUCCESSFUL_PRC, name: "Successful PRC" },
  { value: NAVIGO_STATUS.DECLINED, name: "Declined" },
  //Not in dropdown for selection------
  { value: NAVIGO_STATUS.SCHEDULED, name: "Scheduled" },
  { value: NAVIGO_STATUS.RESCHEDULE_REQUESTED, name: "Reschedule Requested" },
  { value: NAVIGO_STATUS.PHLEBO_ASSIGNED, name: "Phlebo Assigned" },
  { value: NAVIGO_STATUS.PICKUP, name: "Pickup" },
  { value: NAVIGO_STATUS.SAMPLE_COLLECTED, name: "Sample Collected" },
  { value: NAVIGO_STATUS.SAMPLE_RECEIVED, name: "Sample Received" },
  { value: NAVIGO_STATUS.REPORT_GENERATED, name: "Report Generated" },
];

// Lab workflow statuses — hidden from HRA dropdown selection
export const LAB_WORKFLOW_STATUSES = [
  NAVIGO_STATUS.SCHEDULED,
  NAVIGO_STATUS.RESCHEDULE_REQUESTED,
  NAVIGO_STATUS.PHLEBO_ASSIGNED,
  NAVIGO_STATUS.PICKUP,
  NAVIGO_STATUS.SAMPLE_COLLECTED,
  NAVIGO_STATUS.SAMPLE_RECEIVED,
  NAVIGO_STATUS.REPORT_GENERATED,
];

// --------------------
// Helper Functions
// --------------------
export function getStatusName(statusCode) {
  const status = statusList.find((s) => s.value === statusCode);
  return status ? status.name : "Unknown Status";
}

export function isEndStatus(statusCode) {
  return END_STATUS_SET.has(statusCode);
}

export function isEndStatusCompleted(statusCode) {
  return COMPLETED_STATUS_SET.has(statusCode);
}

export function isEndStatusCancelled(statusCode) {
  return CANCELLED_STATUS_SET.has(statusCode);
}

// --------------------
// Payment Status List (Response Shape Unchanged)
// --------------------
export const getAppointmentPaymentStatusList = () => {
  return [
    {
      id: APPOINTMENT_PAYMENT_STATUS.PENDING,
      key: "APPOINTMENT_PAYMENT_STATUS_PENDING",
      label: "Link pending",
    },
    {
      id: APPOINTMENT_PAYMENT_STATUS.INITIATED,
      key: "APPOINTMENT_PAYMENT_STATUS_INITIATED",
      label: "Link requested",
    },
    {
      id: APPOINTMENT_PAYMENT_STATUS.FAILED,
      key: "APPOINTMENT_PAYMENT_STATUS_FAILED",
      label: "Link failed",
    },
    {
      id: APPOINTMENT_PAYMENT_STATUS.COMPLETE,
      key: "APPOINTMENT_PAYMENT_STATUS_COMPLETE",
      label: "Paid",
    },
    {
      id: APPOINTMENT_PAYMENT_STATUS.SPONSORED,
      key: "APPOINTMENT_PAYMENT_STATUS_SPONSORED",
      label: "Sponsored",
    },
    // {
    //   id: APPOINTMENT_PAYMENT_STATUS.LINK_NOT_GENERATED,
    //   key: "APPOINTMENT_PAYMENT_STATUS_LINK_NOT_GENERATED",
    //   label: "Link Not Generated",
    // },
    // {
    //   id: APPOINTMENT_PAYMENT_STATUS.LINK_EXPIRED,
    //   key: "APPOINTMENT_PAYMENT_STATUS_LINK_EXPIRED",
    //   label: "Link Expired",
    // },
    // {
    //   id: APPOINTMENT_PAYMENT_STATUS.LINK_CANCELLED,
    //   key: "APPOINTMENT_PAYMENT_STATUS_LINK_CANCELLED",
    //   label: "Link Cancelled",
    // },
    {
      id: APPOINTMENT_PAYMENT_STATUS.REFUNDED,
      key: "APPOINTMENT_PAYMENT_STATUS_REFUNDED",
      label: "Refunded",
    },
  ];
};

/**
 * Modes that belong to a CONSULTATION service.
 *
 * This is the SERVER's own grouping, not ours: api-appointment-service's
 * models/constants.go lists exactly these three under `// consultation modes`
 * (:212-217), against `// health checkup modes` (:205-210) for Home sample,
 * Centre visit and Camp site visit.
 */
export const CONSULTATION_MODES = Object.freeze([
  MODES.TELE_CONSULTATION,
  MODES.VIDEO_CONSULTATION,
  MODES.IN_PERSON_CONSULTATION,
]);

/**
 * Is this appointment a consultation?
 *
 * The TYPE decides when the row carries one, and the MODE only as a fallback.
 * Neither test works alone:
 *
 *   · `serviceTypeID` is authoritative but not always THERE. It was not always
 *     written ("was not set earlier", appointment_controller.go, 2025-09-03);
 *     it has no omitempty (models/appointment.go:54), so it arrives as 0 rather
 *     than absent, and only reads that enrich backfill it from the catalogue
 *     (repository/appointment.go:1033). Testing it alone silently drops older
 *     consultations — the same trap DASHBOARD_MODES exists to avoid.
 *   · mode alone would read a health checkup as a consultation whenever its
 *     mode is blank.
 *
 * With neither field it answers NO: the note is a prompt, and prompting on a
 * lab collection is the thing being removed here.
 */
export const isConsultation = (appointment) => {
  const type = Number((appointment && appointment.serviceTypeID) || 0);
  if (type) return type === SERVICE_TYPE.CONSULTATION;
  return CONSULTATION_MODES.indexOf((appointment && appointment.mode) || "") !== -1;
};

/**
 * The outcomes that open NO call note: nobody was reached, so there is no
 * conversation to write up — and these are the statuses an operator sets most
 * often in a row, which had made the note a modal to dismiss rather than a thing
 * to fill in. "Not answered" and "Not answered (3 attempts)" (Nittin,
 * 2026-09-25); "Not reachable" (Nittin, 2026-09-28), which is the same outcome
 * by a different route — the call never connected.
 *
 * A LIST, not a chain of !==, because it is now three and the next one should be
 * a one-line change at one place in each repo rather than an edit to a boolean
 * expression at the end of a file.
 */
export const NO_CALL_NOTE_STATUSES = Object.freeze([
  NAVIGO_STATUS.NOT_ANSWERED,
  NAVIGO_STATUS.NOT_ANSWERED_3_ATTEMPTS,
  NAVIGO_STATUS.NOT_REACHABLE,
]);

/**
 * Does a status change open the CALL NOTE?
 *
 * TWO rules. It takes the APPOINTMENT as well as the status so that neither can
 * be applied without the other: this predicate is the whole contract at seven
 * call sites across two repos, and the status-only version it replaces had
 * already been half-applied once (the patient screen opens its own note and was
 * missed).
 *
 *   · CONSULTATIONS ONLY (Nittin, 2026-09-28). A call note records a
 *     consultation call; a home-sample collection or a health checkup has none.
 *   · Every outcome BUT the ones in NO_CALL_NOTE_STATUSES above — the three
 *     where the call did not happen.
 *
 * The note stays reachable BY HAND in every case, from "Add call note". This is
 * about what opens ITSELF, not about what may be written.
 */
export const opensCallNote = (appointment, status) =>
  isConsultation(appointment) &&
  NO_CALL_NOTE_STATUSES.indexOf(Number(status)) === -1;
